import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Transition as _Transition, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "cx-measured-points-filters" }
const _hoisted_2 = { class: "cx-measured-points-filters__form" }
const _hoisted_3 = { class: "cx-measured-points-filters__filter-row" }
const _hoisted_4 = { class: "cx-measured-points-filters__filter-row" }
const _hoisted_5 = { class: "cx-measured-points-filters__filter-row" }
const _hoisted_6 = {
  key: 0,
  class: "cx-measured-points-filters__filter-row"
}
const _hoisted_7 = { class: "cx-measured-points-filters__buttons" }
const _hoisted_8 = { class: "cx-measured-points-filters__results" }
const _hoisted_9 = /*#__PURE__*/_createElementVNode("div", null, null, -1)

import _ from 'lodash';
import { ref, Ref, computed, onMounted, ComputedRef } from 'vue';
import { useStore } from 'vuex';
import { useVuex } from '@vueblocks/vue-use-vuex';
import { UseActions, UseMutations } from '@/store/UseVuex.types';
import useAnimation from '@/composables/useAnimation';
import useLicence from '@/composables/useLicence';
import translationService from '@/services/translation.service';
import CxButton from '@/components/CxButton.vue';
import CxTimeRangeFilter from '@/app/filterPanel/common/CxTimeRangeFilter.vue';
import CxReferenceModelFilter from './components/CxReferenceModelFilter.vue';
import CxUnitNameFilter from './components/CxUnitNameFilter.vue';
import CxUnitTypeFilter from '@/app/filterPanel/common/CxUnitTypeFilter.vue';
import CxMetadataFilters from './components/CxMetadataFilters.vue';
import CxFilterActionPanel from '../common/CxFilterActionPanel.vue';
import CxMenuButton from '@/components/menuComponents/CxMenuButton.vue';
import CxFilterTitle from '../common/CxFilterTitle.vue';
import CxList from '@/components/list/CxList.vue';
import CxListItem from '@/components/list/CxListItem.vue';
import CxIcon from '@/components/CxIcon.vue';
import CxWarning from '@/components/CxWarning.vue';
import CxLabel from '@/components/CxLabel.vue';
import CxCheckbox from '@/components/CxCheckbox.vue';
import { RefModel } from '@/domain/refmodels/RefModel';
import { UnitTypes } from '@/domain/units/UnitTypes.enum';
import { PointSet } from '@/domain/pointSet/PointSet';
import { Unit } from '@/domain/units/Unit';
import FilterMeasuredPointViewModel from '@/store/filterModule/points/modules/filterMeasuredPointViewModel';
import { endOfToday, startOfToday, max, startOfDay, endOfDay, isBefore } from 'date-fns';
import pointApi from '@/http/point.api';
import { v4 as uuidv4 } from 'uuid';
import useWaffleFlag from '@/composables/useWaffleFlag';
import { gettext } from '@/translations/gettext.setup';

type MeasuredPointFiltersResult = {
  id?: string
  title?: string
  warning?: string
}

export default /*#__PURE__*/_defineComponent({
  __name: 'CxMeasuredPointFilters',
  setup(__props, { expose: __expose }) {


const store = useStore();
const { $gettext, interpolate } = gettext;
const { applyAnimations, isAnimationReady } = useAnimation('CxMeasuredPointFilters');
const isFormValid: Ref<boolean> = ref(false);
const unitTypeFilter: Ref<any> = ref(null);
const searching: Ref<boolean> = ref(false);
const notificationsStore = useVuex('notifications', store);
const { error } = notificationsStore.useActions(['error']) as UseActions;

const filterMeasuredPointStore = useVuex('filterPoints/filterMeasuredPoint', store);
const { selectedRefModel } = filterMeasuredPointStore.useGetters(['selectedRefModel']) as { selectedRefModel: ComputedRef<RefModel> };
const { changeSelectedRefModel } = filterMeasuredPointStore.useMutations(['changeSelectedRefModel']) as UseMutations;

const appStore = useVuex('app', store);
const {
  allModels,
  allUnits,
  projectStartDay
} = appStore.useGetters([
  'allModels',
  'allUnits',
  'projectStartDay'
]) as {
  allModels: Ref<RefModel[]>,
  allUnits: Ref<Unit[]>,
  projectStartDay: Ref<string>
};

const mapStore = useVuex('map', store);
const { maxLoadablePointsCount } = mapStore.useGetters(['maxLoadablePointsCount']) as { maxLoadablePointsCount: Ref<number> };
const { startDate, endDate } = filterMeasuredPointStore.useGetters(['startDate', 'endDate']) as { startDate: Ref<string>, endDate: Ref<string> };
const { changeEndDate, changeStartDate } = filterMeasuredPointStore.useMutations(['changeEndDate', 'changeStartDate']) as UseMutations;
const { isPartialPointSet, isLoadedPointsLimitExceeded, resultPointSet } = filterMeasuredPointStore.useGetters(['isPartialPointSet', 'isLoadedPointsLimitExceeded', 'resultPointSet']) as { isPartialPointSet: Ref<boolean>, isLoadedPointsLimitExceeded: Ref<boolean>, resultPointSet: Ref<FilterMeasuredPointViewModel[]> };

const pointSet = computed((): PointSet | null => {
  if (resultPointSet.value.length) return resultPointSet.value[0].data;
  return null;
});

const getResultsFromStore = (): MeasuredPointFiltersResult[] => {
  if (resultPointSet.value && resultPointSet.value.length) {
    return resultPointSet.value.map(r => ({ id: r.id, title: r.name, warning: r.isResultWarning }));
  }
  return [];
};
const results = ref<MeasuredPointFiltersResult[]>(getResultsFromStore());

const getWarningHeader = (): string | null => {
  if (!pointSet.value) return null;
  // As we don't use partial point set concept for UI, we fall back to bulk performance limit warning message.
  if (!isLoadedPointsLimitExceeded.value && !isPartialPointSet.value) return null;
  const title = $gettext('Unable to load %{ totalCount } points because they exceed the performance limit (%{ max }).');
  return interpolate(title, {
    totalCount: pointSet.value.totalCount,
    max: maxLoadablePointsCount.value
  });
};
const translations = computed(() => {
  return {
    clearFilters: $gettext('Clear Filters'),
    search: $gettext('Search'),
    measurementDate: $gettext('Measurement Date'),
    mainFilters: $gettext('Main Filters'),
    metadataFilters: $gettext('Metadata Filters'),
    download: $gettext('Save Point Data As...'),
    warningHeader: getWarningHeader(),
    autolog: $gettext('Include Autologged Points'),
    includeDiscarded: $gettext('Include Deleted Points'),
    display: $gettext('Display'),
    result: $gettext('Results'),
    noResult: $gettext('No results'),
    errorMessage: $gettext('Fetching data for Measured Points Filtering Panel failed. Try reopening the panel or contact support.')
  };
});

const projectUuid: ComputedRef<string> = computed(() => store.getters.projectUuid);

const activeDaysLoading: Ref<boolean> = ref(false);
const activeDays: Ref<Date[]> = ref([]);
const fetchActiveDays = async () => {
  activeDaysLoading.value = true;
  try {
    activeDays.value = await pointApi.getActiveDays(projectUuid.value);
    if (!startDate.value || !endDate.value) {
      if (activeDays.value.length > 0) {
        const dateFrom = startOfDay(max(activeDays.value));
        const dateTo = endOfDay(dateFrom);
        changeStartDate(dateFrom);
        changeEndDate(dateTo);
      } else {
        changeStartDate(startOfToday());
        changeEndDate(endOfToday());
      }
    }
  } catch (err) {
    error({ exception: err, message: translations.value.errorMessage });
  } finally {
    activeDaysLoading.value = false;
  }
};

const listHeader = computed((): string => {
  return results.value.length ? translations.value.result : translations.value.noResult;
});

const { selectedUnitNames,
  selectedUnitTypes,
  availableUnitNames } = filterMeasuredPointStore.useGetters([
    'selectedUnitNames',
    'selectedUnitTypes',
    'availableUnitNames'
  ]) as {
    selectedUnitNames: Ref<Unit[]>,
    selectedUnitTypes: Ref<UnitTypes[]>,
    availableUnitNames: Ref<Unit[]>
  };

const { changeUnitNames } = filterMeasuredPointStore.useMutations(['changeUnitNames']) as UseMutations;
const { changeSelectedUnitTypes } = filterMeasuredPointStore.useActions(['changeSelectedUnitTypes']) as UseActions;
const availableUnitTypes = computed((): { id: UnitTypes, name: string } => {
  const uniqueTypeUnits = _.uniqBy(allUnits.value, 'entityType');
  return uniqueTypeUnits.map((u) => {
    return {
      id: u.entityType,
      name: translationService.get(u.entityType)
    };
  });
});

const { availableMetadataFilters,
  addedMetadataFilters } = filterMeasuredPointStore.useGetters([
    'availableMetadataFilters',
    'addedMetadataFilters'
  ]) as {
    availableMetadataFilters: Ref<{ fieldName: string, dataType: string, displayName: string }[]>,
    addedMetadataFilters: Ref<string[]>
  };

const {
  removeMetadataFilter,
  changeMetadataFilterValues,
  changeMetadataFilterComparators
} = filterMeasuredPointStore.useMutations([
  'removeMetadataFilter',
  'changeMetadataFilterValues',
  'changeMetadataFilterComparators',
]) as UseMutations;

const { addMetadataFilter, exportPoints } = filterMeasuredPointStore.useActions(['addMetadataFilter', 'exportPoints']) as UseActions;

const { queryOngoing, isExportLoading } = filterMeasuredPointStore.useGetters(['queryOngoing', 'isExportLoading']) as { queryOngoing: Ref<boolean>, isExportLoading: Ref<boolean> };

const datesValid = computed((): boolean => {
  return isBefore(new Date(startDate.value), new Date(endDate.value));
});

const {
  includeAutologPoints,
  includeDiscardedPoints,
} = filterMeasuredPointStore.useGetters([
  'includeAutologPoints',
  'includeDiscardedPoints',
]) as {
  includeAutologPoints: Ref<boolean>,
  includeDiscardedPoints: Ref<boolean>,
};
const {
  clearFilters,
  setIncludeAutologPoints,
  setIncludeDiscardedPoints
} = filterMeasuredPointStore.useMutations([
  'clearFilters',
  'setIncludeAutologPoints',
  'setIncludeDiscardedPoints'
]) as UseMutations;
const { displayMeasuredPoints, searchMeasuredPoints } = filterMeasuredPointStore.useActions(['displayMeasuredPoints', 'searchMeasuredPoints']) as UseActions;
const autologPointsChecked = computed({
  get() { return includeAutologPoints.value; },
  set(value) { setIncludeAutologPoints(value); }
});
const discardedPointsChecked = computed({
  get() { return includeDiscardedPoints.value; },
  set(value) { setIncludeDiscardedPoints(value); }
});
const onClearFiltersClickedHandler = () => {
  clearFilters();
  results.value = [];
  unitTypeFilter.value.clearChipsInput();
};
const search = async () => {
  searching.value = true;
  try {
    const total_count = await searchMeasuredPoints();
    if (total_count > 0) {
      results.value = [{
        id: uuidv4(),
        title: `${total_count} points`
      }];
    } else {
      results.value = [];
    }
  } catch (err) {
    error({ exception: err, message: translations.value.errorMessage });
  } finally {
    searching.value = false;
  }
};

const onApplyClickedHandler = () => {
  displayMeasuredPoints();
};

onMounted(async () => {
  await fetchActiveDays();
});

__expose({
  getWarningHeader,
  fetchActiveDays
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter2')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", null, [
            _createVNode(CxFilterTitle, {
              title: translations.value.measurementDate,
              rank: 4
            }, null, 8, ["title"]),
            _createVNode(CxTimeRangeFilter, {
              startDate: _unref(startDate),
              changeStartDate: _unref(changeStartDate),
              endDate: _unref(endDate),
              changeEndDate: _unref(changeEndDate),
              projectStartDay: _unref(projectStartDay),
              events: activeDays.value,
              loading: activeDaysLoading.value
            }, null, 8, ["startDate", "changeStartDate", "endDate", "changeEndDate", "projectStartDay", "events", "loading"])
          ], 512), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter2')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_3, [
            _createVNode(CxFilterTitle, {
              title: translations.value.mainFilters,
              rank: 4
            }, null, 8, ["title"]),
            _createVNode(CxReferenceModelFilter, {
              refmodels: _unref(allModels),
              selectedRefModel: _unref(selectedRefModel),
              changeSelectedRefModel: _unref(changeSelectedRefModel)
            }, null, 8, ["refmodels", "selectedRefModel", "changeSelectedRefModel"])
          ], 512), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter2')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_4, [
            _createVNode(CxUnitTypeFilter, {
              availableUnitTypes: availableUnitTypes.value,
              selectedUnitTypes: _unref(selectedUnitTypes),
              changeSelectedUnitTypes: _unref(changeSelectedUnitTypes),
              ref_key: "unitTypeFilter",
              ref: unitTypeFilter
            }, null, 8, ["availableUnitTypes", "selectedUnitTypes", "changeSelectedUnitTypes"]),
            _createVNode(CxUnitNameFilter, {
              availableUnits: _unref(availableUnitNames),
              selectedUnits: _unref(selectedUnitNames),
              updateSelection: _unref(changeUnitNames)
            }, null, 8, ["availableUnits", "selectedUnits", "updateSelection"])
          ], 512), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter2')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_5, [
            _createVNode(CxCheckbox, {
              checked: autologPointsChecked.value,
              "onUpdate:checked": _cache[0] || (_cache[0] = ($event: any) => ((autologPointsChecked).value = $event)),
              label: translations.value.autolog
            }, null, 8, ["checked", "label"]),
            _createVNode(CxCheckbox, {
              checked: discardedPointsChecked.value,
              "onUpdate:checked": _cache[1] || (_cache[1] = ($event: any) => ((discardedPointsChecked).value = $event)),
              label: translations.value.includeDiscarded
            }, null, 8, ["checked", "label"])
          ], 512), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter3')
      }, {
        default: _withCtx(() => [
          (_unref(availableMetadataFilters).length > 0)
            ? _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_6, [
                _createVNode(CxFilterTitle, {
                  title: translations.value.metadataFilters,
                  rank: 4
                }, null, 8, ["title"]),
                _createVNode(CxMetadataFilters, {
                  availableMetadataFilters: _unref(availableMetadataFilters),
                  addedMetadataFilters: _unref(addedMetadataFilters),
                  addMetadataFilter: _unref(addMetadataFilter),
                  removeMetadataFilter: _unref(removeMetadataFilter),
                  changeMetadataFilterValues: _unref(changeMetadataFilterValues),
                  changeMetadataFilterComparators: _unref(changeMetadataFilterComparators)
                }, null, 8, ["availableMetadataFilters", "addedMetadataFilters", "addMetadataFilter", "removeMetadataFilter", "changeMetadataFilterValues", "changeMetadataFilterComparators"])
              ], 512)), [
                [_vShow, _unref(isAnimationReady)]
              ])
            : _createCommentVNode("", true)
        ]),
        _: 1
      }, 8, ["name"]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('filter4')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_7, [
            _createVNode(CxButton, {
              disabled: false,
              onClick: onClearFiltersClickedHandler,
              variantSecondary: "",
              tooltip: translations.value.clearFilters
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(translations.value.clearFilters), 1)
              ]),
              _: 1
            }, 8, ["tooltip"]),
            _createVNode(CxButton, {
              isDisabled: !isFormValid.value || !datesValid.value,
              onClick: search,
              variantPrimary: "",
              tooltip: translations.value.search,
              loading: searching.value
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(translations.value.search), 1)
              ]),
              _: 1
            }, 8, ["isDisabled", "tooltip", "loading"])
          ], 512), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"]),
      _createElementVNode("div", _hoisted_8, [
        _createVNode(_Transition, {
          name: _unref(applyAnimations)('filter5')
        }, {
          default: _withCtx(() => [
            _withDirectives(_createVNode(CxList, {
              items: results.value,
              header: listHeader.value
            }, {
              default: _withCtx(({ item, selected }) => [
                _createVNode(CxListItem, {
                  item: item,
                  selected: selected,
                  disabled: true
                }, {
                  selection: _withCtx(() => [
                    _hoisted_9
                  ]),
                  "before-content": _withCtx(({}) => [
                    _createVNode(CxIcon, {
                      icon: "cx_points",
                      size: 15,
                      class: "cxu-color-faded"
                    })
                  ]),
                  _: 2
                }, 1032, ["item", "selected"])
              ]),
              "header-append": _withCtx(({}) => [
                (_unref(isLoadedPointsLimitExceeded))
                  ? (_openBlock(), _createBlock(CxWarning, {
                      key: 0,
                      warning: translations.value.warningHeader || ''
                    }, null, 8, ["warning"]))
                  : _createCommentVNode("", true)
              ]),
              placeholder: _withCtx(() => [
                _createVNode(CxLabel, {
                  class: "cx-simple-list__placeholder results-placeholder",
                  label: translations.value.noResult
                }, null, 8, ["label"])
              ]),
              _: 1
            }, 8, ["items", "header"]), [
              [_vShow, _unref(isAnimationReady)]
            ])
          ]),
          _: 1
        }, 8, ["name"])
      ]),
      _createVNode(_Transition, {
        name: _unref(applyAnimations)('action')
      }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(CxFilterActionPanel, {
            onApplyClicked: onApplyClickedHandler,
            applyText: translations.value.display,
            applyTooltip: translations.value.display,
            isDisabled: !results.value.length,
            queryOngoing: _unref(queryOngoing),
            isClearFiltersVisible: false
          }, {
            default: _withCtx(() => [
              _createVNode(CxMenuButton, {
                text: translations.value.download,
                items: [{name: 'csv'}, {name: 'json'}, {name: 'geo'}],
                loading: _unref(isExportLoading),
                disabled: !results.value.length,
                menuPosition: "top",
                onClick: _cache[2] || (_cache[2] = (selectedExtension) => _unref(exportPoints)(selectedExtension.name))
              }, null, 8, ["text", "loading", "disabled"])
            ]),
            _: 1
          }, 8, ["applyText", "applyTooltip", "isDisabled", "queryOngoing"]), [
            [_vShow, _unref(isAnimationReady)]
          ])
        ]),
        _: 1
      }, 8, ["name"])
    ])
  ]))
}
}

})